import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { emitVisitCompleteWhenPhotosSettle, emitVisitError } from '../lib/embedBridge';
import { useHandoffSeed, skipHandoffContact } from '@/lib/handoff';
import { usePreview, PreviewInterstitial } from './preview/PreviewShell';
import QuestionStep from './intake/QuestionStep';
import MultiSelectStep from './intake/MultiSelectStep';
import MultiSelectWithAddonsStep from './intake/MultiSelectWithAddonsStep';
import InputStep from './intake/InputStep';
import HeightWeightStep from './intake/HeightWeightStep';
import HeightWeightBmiStep from './intake/HeightWeightBmiStep';
import TextareaStep from './intake/TextareaStep';
import TextareaWithUploadStep from './intake/TextareaWithUploadStep';
import GlpCatalogCheckoutStep from './intake/GlpCatalogCheckoutStep';
import GlpPreviewCheckoutStep from './preview/GlpPreviewCheckoutStep';
import SuccessStep from './intake/SuccessStep';
import DisqualifiedStep from './intake/DisqualifiedStep';
import BasicContactStep from './intake/BasicContactStep';
import ConsentDropdownStep from './intake/ConsentDropdownStep';
import MedicationSelectionStep from './intake/MedicationSelectionStep';
import FileUploadStep from './intake/FileUploadStep';
import CompleteStep from './intake/CompleteStep';
import ShippingAddressStep from './intake/ShippingAddressStep';
import RegimenDropdownStep from './intake/RegimenDropdownStep';
import DecisionWithRegimenStep from './intake/DecisionWithRegimenStep';
import RegimenInfoDisplayStep from './intake/RegimenInfoDisplayStep';
import Footer from './intake/Footer';
import BrandLogo from '@/components/BrandLogo';
import { useTenantBranding } from '@/context/TenantBranding';
import { buildTenantUrl } from '@/lib/tenant';
import {
  mainQuestion,
  getFlowByGoal, 
  DISQUALIFYING_CONDITIONS, 
  DISQUALIFYING_GLP_ALLERGIES, 
  DISQUALIFYING_DIABETES_MEDS,
  CONDITIONS_REQUIRING_FOLLOWUP,
  CONDITIONS_REQUIRING_CONSENT,
  conditionalQuestions,
  patientOnTreatmentQuestions,
  newPatientQuestions
} from '../data/intakeData';
import { apiService } from '../services/api';
import { consentAnswerLabel, describeSubmitFailure, formatMultiSelectAnswer, splitHeightWeightAnswer } from '../lib/utils';
import { MutatingDots } from 'react-loader-spinner'

const STORAGE_KEY = 'fitpal_intake_progress';

// Resume modal component
const ResumeModal = ({ onResume, onStartFresh }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Welcome back!
        </h3>
        <p className="text-gray-500">
          We found your previous progress. Would you like to continue where you left off?
        </p>
      </div>
      <div className="space-y-3">
        <button
          onClick={onResume}
          className="btn-primary w-full"
        >
          Continue where I left off
        </button>

        
        <button
          onClick={onStartFresh}
          className="w-full py-3 px-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
          disabled={localStorage.getItem('fitpal_intake_payment_id')!==null && localStorage.getItem('fitpal_intake_payment_id')!==undefined &&  localStorage.getItem('fitpal_intake_payment_id')!=='' }
        >
          Start fresh
        </button>
      </div>
    </div>
  </div>
);

const HealthIntakeForm = () => {
  const { dashboardUrl } = useTenantBranding();
  const [primaryGoal, setPrimaryGoal] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  // §authenticated-patient handoff: prefill contact from the resolved ?pt= patient.
  useHandoffSeed(setAnswers);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  // §concierge-oto: the created visit (masterId etc.) for the post-payment offer.
  const [visitResult, setVisitResult] = useState(null);
  const [slideDirection, setSlideDirection] = useState('initial');
  const [slideKey, setSlideKey] = useState(0);
  const [paymentData, setPaymentData] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [dynamicFlow, setDynamicFlow] = useState([]);
  // Captured from /beluga/create-visit-v2 response so post-checkout photo
  // slides (full-body-image, identity-verification) can POST to
  // /beluga/upload-visit-photo-submission with the right routing keys.
  const [visitIdentifiers, setVisitIdentifiers] = useState(() => {
    try {
      const cached = localStorage.getItem('fitpal_intake_visit_ids');
      return cached ? JSON.parse(cached) : { masterId: null, visitId: null };
    } catch (e) {
      return { masterId: null, visitId: null };
    }
  });
  // Track which post-checkout photo slides have already uploaded, so we don't
  // re-POST on back-navigation or re-mount.
  const [uploadedPhotoSlides, setUploadedPhotoSlides] = useState(new Set());

  // Single-select auto-advance gating (§FE_PRD_SINGLE_SELECT_AUTOADVANCE_RACE).
  // advancedForSlideKeyRef ensures we schedule at most ONE advance per slideKey,
  // so a user who changes their selection on the same slide cannot stack timers
  // and accidentally skip the next slide. advanceTimerRef holds the pending
  // timer id so it can be cancelled on unmount.
  const advancedForSlideKeyRef = useRef(null);
  const advanceTimerRef = useRef(null);
  useEffect(() => () => {
    if (advanceTimerRef.current !== null) clearTimeout(advanceTimerRef.current);
  }, []);

  // Load saved progress from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.primaryGoal && parsed.currentStep > 0) {
          setSavedProgress(parsed);
          setShowResumeModal(true);
        }
      }
    } catch (e) {
      console.error('Error loading saved progress:', e);
    }
  }, []);

  // Save progress to localStorage whenever state changes
  useEffect(() => {
    if (primaryGoal && currentStep > 0 && !isComplete && !isDisqualified) {
      try {
        const progressData = {
          primaryGoal,
          currentStep,
          answers,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(progressData));
      } catch (e) {
        console.error('Error saving progress:', e);
      }
    }
  }, [primaryGoal, currentStep, answers, isComplete, isDisqualified]);

  // Clear saved progress when form is completed
  const clearSavedProgress = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('fitpal_intake_payment_id');
      localStorage.removeItem('fitpal_intake_form_is_back_clicked')
    } catch (e) {
      console.error('Error clearing progress:', e);
    }
  }, []);

  // Resume from saved progress
  const handleResumeProgress = useCallback(() => {
    if (savedProgress) {
      setPrimaryGoal(savedProgress.primaryGoal);
      setCurrentStep(savedProgress.currentStep);
      setAnswers(savedProgress.answers || {});
      setShowResumeModal(false);
      // Rebuild dynamic flow based on answers
      const baseFlow = getFlowByGoal(savedProgress.primaryGoal);
      setDynamicFlow(buildDynamicFlow(baseFlow, savedProgress.answers || {}));
    }
    // buildDynamicFlow is a stable inline useCallback declared below; including
    // it here would force handleResumeProgress to re-create on every flow
    // rebuild, defeating its memoization purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedProgress]);

  // Start fresh
  const handleStartFresh = useCallback(() => {
    clearSavedProgress();
    setShowResumeModal(false);
    setSavedProgress(null);
  }, [clearSavedProgress]);

  // Build dynamic flow based on answers (handles conditional questions)
  const buildDynamicFlow = useCallback((baseFlow, currentAnswers) => {
    let flow = [...baseFlow];
    
    // Check medical conditions for consent requirements
    const medicalConditions = currentAnswers['medical-conditions'] || [];
    
    // Insert consent questions based on medical conditions selected
    if (Array.isArray(medicalConditions)) {
      const medCondIndex = flow.findIndex(q => q.id === 'medical-conditions');
      if (medCondIndex !== -1) {
        let insertOffset = 1;
        
        // Check for gallstone consent (asymptomatic gallstones)
        if (medicalConditions.includes('asymptomatic-gallstones') && !flow.find(q => q.id === 'gallstone-consent')) {
          flow.splice(medCondIndex + insertOffset, 0, conditionalQuestions.gallstoneConsentQuestion);
          insertOffset++;
        }
        
        // Check for cholecystectomy consent (past removal of gallbladder)
        if (medicalConditions.includes('cholecystectomy') && !flow.find(q => q.id === 'cholecystectomy-consent')) {
          flow.splice(medCondIndex + insertOffset, 0, conditionalQuestions.cholecystectomyConsentQuestion);
          insertOffset++;
        }
        
        // Check for thyroid consent
        if (medicalConditions.includes('thyroid-issues') && !flow.find(q => q.id === 'thyroid-consent')) {
          flow.splice(medCondIndex + insertOffset, 0, conditionalQuestions.thyroidConsentQuestion);
          insertOffset++;
        }
      }
    }

    // BMI Consent — fires when computed BMI ∈ [25, 30) AND no comorbidity is
    // selected on medical-conditions. Drive doc Q6 (off-label use). Inserted
    // after height-weight so the patient sees it once their BMI is computed.
    const heightWeight = currentAnswers['height-weight'];
    const computedBmi = heightWeight && typeof heightWeight === 'object' ? Number(heightWeight.bmi) : NaN;
    const bmiInOffLabelRange = Number.isFinite(computedBmi) && computedBmi >= 25 && computedBmi < 30;
    const hasComorbidity = Array.isArray(medicalConditions)
      && medicalConditions.some(c => c && c !== 'none');
    if (bmiInOffLabelRange && !hasComorbidity && !flow.find(q => q.id === 'bmi-consent')) {
      const hwIndex = flow.findIndex(q => q.id === 'height-weight');
      if (hwIndex !== -1) {
        flow.splice(hwIndex + 1, 0, conditionalQuestions.bmiConsentQuestion);
      }
    }

    // Check if medical condition details are needed
    const requiresFollowup = Array.isArray(medicalConditions) &&
      medicalConditions.some(cond => CONDITIONS_REQUIRING_FOLLOWUP.includes(cond));
    if (requiresFollowup) {
      // Find where to insert - after any consent questions
      const possiblePrevIds = ['thyroid-consent', 'cholecystectomy-consent', 'gallstone-consent', 'medical-conditions'];
      let insertIndex = -1;
      for (const id of possiblePrevIds) {
        const idx = flow.findIndex(q => q.id === id);
        if (idx !== -1) {
          insertIndex = idx + 1;
          break;
        }
      }
      
      if (insertIndex !== -1 && !flow.find(q => q.id === 'medical-condition-details')) {
        flow.splice(insertIndex, 0, conditionalQuestions.medicalConditionDetailsQuestion);
      }
    }

    // Handle GLP medication selection - determines which flow to show
    const currentGlpMeds = currentAnswers['current-glp-medications'] || [];
    const isOnTreatment = Array.isArray(currentGlpMeds) && 
      (currentGlpMeds.includes('semaglutide') || currentGlpMeds.includes('tirzepatide'));
    const isNoneSelected = Array.isArray(currentGlpMeds) && currentGlpMeds.includes('none');
    
    // Determine medication type for titration regimens
    const currentDose = currentAnswers['current-dose'] || '';
    const isSemaglutide = currentDose.startsWith('semaglutide') || currentGlpMeds.includes('semaglutide');
    const isTirzepatide = currentDose.startsWith('tirzepatide') || currentGlpMeds.includes('tirzepatide');

    // Find the index of current-glp-medications
    const glpIndex = flow.findIndex(q => q.id === 'current-glp-medications');

    if (glpIndex !== -1) {
      // Remove existing new patient questions (they're in the base flow)
      const newPatientIds = ['injection-capability'];
      flow = flow.filter(q => !newPatientIds.includes(q.id));
      
      // Remove existing patient on treatment questions
      const onTreatmentIds = [
        'last-medication-date', 'dose-decrease', 'restart-medication',
        'last-medication-date-input', 'current-dose', 'brand-or-compounded',
        'medication-details', 'side-effects', 'fatigue-additive', 'nausea-additive', 'muscle-additive',
        'medication-progress', 'rapid-titration', 'twice-weekly', 'dosage-preference',
        'prescription-photo', 'upload-prescription',
        'regimen-selection-alternative', 'regimen-selection-rapid', 'regimen-selection-twice-weekly'
      ];
      flow = flow.filter(q => !onTreatmentIds.includes(q.id));

      // Find new insertion point after filtering
      const newGlpIndex = flow.findIndex(q => q.id === 'current-glp-medications');

      if (isOnTreatment) {
        // Build PATIENT ON TREATMENT flow with conditional questions
        let treatmentFlow = [conditionalQuestions.lastMedicationDateQuestion];
        
        // Add dose decrease or restart medication consent based on last medication date
        const lastMedDate = currentAnswers['last-medication-date'];
        if (lastMedDate === 'Within the past 2-3 weeks' || lastMedDate === 'Within the past month') {
          treatmentFlow.push(conditionalQuestions.doseDecreaseQuestion);
        } else if (lastMedDate === 'It has been over a month' || lastMedDate === 'It has been over 2 months') {
          treatmentFlow.push(conditionalQuestions.restartMedicationQuestion);
        }
        
        treatmentFlow.push(conditionalQuestions.lastMedicationDateInputQuestion);
        treatmentFlow.push(conditionalQuestions.currentDoseQuestion);
        treatmentFlow.push(conditionalQuestions.brandOrCompoundedQuestion);
        
        // Add medication details only if compounded
        const brandOrCompounded = currentAnswers['brand-or-compounded'];
        if (brandOrCompounded === 'compounded') {
          treatmentFlow.push(conditionalQuestions.medicationDetailsQuestion);
        }
        
        treatmentFlow.push(conditionalQuestions.sideEffectsQuestion);
        
        treatmentFlow.push(conditionalQuestions.medicationProgressQuestion);
        
        // Add decision questions based on medication progress answer
        // The regimen dropdowns are now embedded in the decision questions
        const medicationProgress = currentAnswers['medication-progress'];
        
        if (medicationProgress === "I'm not feeling any effects and have no side effects") {
          // Question 1a - Rapid Titration with embedded regimen dropdowns
          treatmentFlow.push(conditionalQuestions.rapidTitrationQuestion);
        } else if (medicationProgress === "I'm not feeling any effects but I'm having side effects" || medicationProgress === "I'm feeling the effects but have side effects") {
          // Question 1b - Twice Weekly with embedded regimen dropdowns
          treatmentFlow.push(conditionalQuestions.twiceWeeklyQuestion);
        } else if (medicationProgress === "I'm feeling the effects and have no side effects") {
          // Show alternative regimen info directly
          treatmentFlow.push(conditionalQuestions.alternativeRegimenInfoQuestion);
        }
        
        treatmentFlow.push(conditionalQuestions.dosagePreferenceQuestion);
        treatmentFlow.push(conditionalQuestions.prescriptionPhotoQuestion);
        
        // Add upload prescription if yes
        const prescriptionAnswer = currentAnswers['prescription-photo'];
        if (prescriptionAnswer === 'yes') {
          treatmentFlow.push(conditionalQuestions.uploadPrescriptionQuestion);
        }
        
        flow.splice(newGlpIndex + 1, 0, ...treatmentFlow);
      } else if (isNoneSelected) {
        // Insert NEW PATIENT flow - simplified injection question
        const newFlow = [
          conditionalQuestions.injectionCapabilityQuestion
        ];
        flow.splice(newGlpIndex + 1, 0, ...newFlow);
      }
    }

    return flow;
  }, []);

  // Get the current flow based on selected primary goal
  const preview = usePreview();
  const currentFlow = useMemo(() => {
    if (!primaryGoal) return [];
    const base = dynamicFlow.length > 0 ? dynamicFlow : getFlowByGoal(primaryGoal);
    // §authenticated-patient handoff: drop the basic-contact step for a known patient.
    // §preview: marketing slides between questions (question order unchanged).
    return preview.wrapFlow(skipHandoffContact(base));
  }, [primaryGoal, dynamicFlow, preview]);

  // Update dynamic flow when answers change
  useEffect(() => {
    if (primaryGoal) {
      const baseFlow = getFlowByGoal(primaryGoal);
      const newFlow = buildDynamicFlow(baseFlow, answers);
      setDynamicFlow(newFlow);
    }
  }, [primaryGoal, answers, buildDynamicFlow]);

  const totalSteps = currentFlow.length;
  const currentQuestion = currentFlow[currentStep];
  // §preview: the omzo-style checkout (term picker) replaces the live one only on /preview/glp1.
  const GlpCheckout = preview.active ? GlpPreviewCheckoutStep : GlpCatalogCheckoutStep;

  // Get flow title based on primary goal
  const getFlowTitle = () => {
    return 'GLP-1 Weight Loss Program';
  };

  // Check for disqualifying answers
  const checkDisqualification = useCallback((questionId, answer) => {
    // Check medical conditions
    if (questionId === 'medical-conditions' && Array.isArray(answer)) {
      const disqualifyingSelected = answer.filter(a => DISQUALIFYING_CONDITIONS.includes(a));
      if (disqualifyingSelected.length > 0) {
        return {
          disqualified: true,
          reason: 'Based on the medical conditions you selected, you may not be a candidate for GLP-1 treatment. Please consult with your healthcare provider.'
        };
      }
    }

    // Check GLP allergies
    if (questionId === 'glp-allergies' && Array.isArray(answer)) {
      const disqualifyingSelected = answer.filter(a => DISQUALIFYING_GLP_ALLERGIES.includes(a));
      if (disqualifyingSelected.length > 0) {
        return {
          disqualified: true,
          reason: 'Due to your reported allergies to GLP-1 medications, you may not be a candidate for this treatment.'
        };
      }
    }

    // Check diabetes medications
    if (questionId === 'diabetes-medications' && Array.isArray(answer)) {
      const disqualifyingSelected = answer.filter(a => DISQUALIFYING_DIABETES_MEDS.includes(a));
      if (disqualifyingSelected.length > 0) {
        return {
          disqualified: true,
          reason: 'Due to your current diabetes medications, GLP-1 treatment may not be appropriate for you. Please consult with your healthcare provider.'
        };
      }
    }

    // Check gastric bypass
    if (questionId === 'gastric-bypass' && answer === 'yes') {
      return {
        disqualified: true,
        reason: 'Patients who have had gastric bypass surgery within the past 6 months are not eligible for this treatment.'
      };
    }

    // injection-capability 'no' is NOT a disqualifier — it hands off to the
    // branded intake (oral options exist there); see handleAnswer.

    // Check consent declines (all consent forms)
    const consentQuestionIds = [
      'pregnancy-consent', 
      'gallstone-consent', 
      'cholecystectomy-consent', 
      'thyroid-consent',
      'treatment-consent', 
      'final-verification',
      'truthfulness-consent',
      'glp1-consent'
    ];
    if (consentQuestionIds.includes(questionId) && answer === 'decline') {
      return {
        disqualified: true,
        reason: 'You have indicated that you do not wish to continue with this treatment.'
      };
    }

    // Check BMI (handled in HeightWeightBmiStep)
    if (questionId === 'height-weight' && answer?.bmi) {
      const bmi = parseFloat(answer.bmi);
      const currentGlpMeds = answers['current-glp-medications'] || [];
      const isOnTreatment = Array.isArray(currentGlpMeds) && 
        (currentGlpMeds.includes('semaglutide') || currentGlpMeds.includes('tirzepatide'));

      if (isOnTreatment && bmi < 20) {
        return {
          disqualified: true,
          reason: 'Based on your BMI calculation, you may not be a candidate for continued GLP-1 treatment. Please consult with your healthcare provider.'
        };
      } else if (!isOnTreatment && bmi < 25) {
        return {
          disqualified: true,
          reason: 'Based on your BMI calculation (below 25), you may not meet the criteria for GLP-1 weight loss treatment. Please consult with your healthcare provider for other options.'
        };
      }
    }

    return { disqualified: false };
  }, [answers]);

  // Trigger slide animation
  const triggerSlide = (direction) => {
    setSlideDirection(direction);
    setSlideKey(prev => prev + 1);
  };

  const handlePrimaryGoalSelect = useCallback((goal) => {
    setPrimaryGoal(goal);
    setAnswers({ 'weight-loss-goal': goal });
    setCurrentStep(0);
    setDynamicFlow(getFlowByGoal(goal));
    triggerSlide('forward');
  }, []);

  const handleDisqualify = useCallback((reason) => {
    setIsDisqualified(true);
    setDisqualifyReason(reason);
    clearSavedProgress();
    triggerSlide('forward');
  }, [clearSavedProgress]);

  const handleAnswer = useCallback(async(questionId, answer) => {
    const previousAnswer = answers[questionId];

    // Oral-preference handoff: the customized (compounded injectable) program
    // has no oral products, but the branded flow does (Foundayo orforglipron
    // tablets) and its injection-ability step routes 'no-need-oral' to them.
    // Instead of disqualifying, send the patient to the branded intake,
    // preserving the tenant key (?t=) and embed mode. Runs BEFORE setAnswers
    // so the save-progress effect can't re-persist the abandoned /glp1
    // session between clearSavedProgress() and the navigation unload.
    if (questionId === 'injection-capability' && answer === 'no') {
      let target = buildTenantUrl('/branded-weight-loss');
      try {
        if (new URLSearchParams(window.location.search).get('embed') === '1') {
          target += (target.includes('?') ? '&' : '?') + 'embed=1';
        }
      } catch (e) { /* fall through with the base target */ }
      clearSavedProgress();
      window.location.assign(target);
      return;
    }

    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));

    // Check for disqualification
    const disqualResult = checkDisqualification(questionId, answer);
    if (disqualResult.disqualified) {
      handleDisqualify(disqualResult.reason);
      return;
    }

    // Post-checkout photo relay. /beluga/create-visit-v2 has already created
    // the Beluga visit by the time these slides are answered, so we POST each
    // photo to /beluga/upload-visit-photo-submission with slideId so the
    // backend can forward it to Beluga via setVisitFile. Fire-and-forget so
    // navigation isn't blocked; the local DB row is the source of truth and
    // a failed Beluga relay can be retried server-side.
    const photoSlides = ['identity-verification', 'full-body-image'];
    if (
      photoSlides.includes(questionId) &&
      answer && answer.preview &&
      !uploadedPhotoSlides.has(questionId)
    ) {
      const ids = visitIdentifiers.masterId
        ? visitIdentifiers
        : (() => {
            try {
              return JSON.parse(localStorage.getItem('fitpal_intake_visit_ids') || '{}');
            } catch (e) {
              return {};
            }
          })();
      if (ids && ids.masterId) {
        setUploadedPhotoSlides(prev => new Set(prev).add(questionId));
        apiService
          .uploadVisitPhoto({
            masterId: ids.masterId,
            visitId: ids.visitId,
            slideId: questionId,
            photo: answer,
          })
          .catch((err) => {
            // Non-blocking: backend autosave still persists the photo into
            // quiz_data, so the photo is recoverable. Surface to console for
            // debugging without alarming the patient.
            console.warn(`[uploadVisitPhoto] ${questionId} relay failed:`, err?.response?.data || err.message);
            setUploadedPhotoSlides(prev => {
              const next = new Set(prev);
              next.delete(questionId);
              return next;
            });
          });
      } else {
        console.warn(`[uploadVisitPhoto] ${questionId}: no masterId in state/localStorage; skipping Beluga relay (autosave still persists)`);
      }
    }

    // Auto-advance for single-select questions ONLY if it's a new selection.
    // Single-fire per slideKey so rapid re-taps on the same slide cannot stack
    // timers and skip the next slide. See FE_PRD_SINGLE_SELECT_AUTOADVANCE_RACE.
    // Covers both 'single-select' and 'single-select-conditional' types.
    if ((currentQuestion?.type === 'single-select' || currentQuestion?.type === 'single-select-conditional') && answer !== previousAnswer) {
      if (advancedForSlideKeyRef.current !== slideKey) {
        advancedForSlideKeyRef.current = slideKey;
        if (advanceTimerRef.current !== null) clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = setTimeout(() => {
          advanceTimerRef.current = null;
          setCurrentStep(prev => {
            if (prev >= totalSteps - 1) return prev;
            setSlideDirection('forward');
            setSlideKey(k => k + 1);
            return prev + 1;
          });
        }, 200);
      }
    }





  
  localStorage.setItem('fitpal_intake_form_is_back_clicked','no')
  }, [totalSteps, currentQuestion, answers, checkDisqualification, handleDisqualify, clearSavedProgress, visitIdentifiers, uploadedPhotoSlides, slideKey]);

  useEffect(() => {
    // Autosave intake form to backend. Debounced 2s and cancelled on dep change
    // so rapid typing/clicking coalesces into a single write per quiet period.
    const urlParams = new URLSearchParams(window.location.search);
    const payment_id = urlParams.get('payment_id');

    if (!payment_id || primaryGoal === null) return undefined;

    const timer = setTimeout(() => {
      if (localStorage.getItem('fitpal_intake_form_is_back_clicked') !== 'yes') {
        apiService.updateForm(answers, primaryGoal, currentStep, Date.now(), payment_id);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [answers, primaryGoal, currentStep])

  useEffect(()=>{

        //storing data backend on complete visit start

    const queryString = window.location.search;

    // 2. Create a URLSearchParams object
    const urlParams = new URLSearchParams(queryString);

    const payment_id=urlParams.get('payment_id');

    if(payment_id!==null &&  payment_id!==undefined &&  payment_id!=='' )
    {
     

       const getStoredData=JSON.parse(localStorage.getItem(STORAGE_KEY))

       setAnswers(getStoredData.answers)
   
    
    }
    

  },[])

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setSlideDirection('forward');
      setSlideKey(prev => prev + 1);
      setCurrentStep(prev => prev + 1);
    }
    
  }, [currentStep, totalSteps]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setSlideDirection('backward');
      setSlideKey(prev => prev + 1);
      setCurrentStep(prev => prev - 1);
    } else if (primaryGoal) {
      // Go back to weight loss goal question (main question)
      setSlideDirection('backward');
      setSlideKey(prev => prev + 1);
      setPrimaryGoal(null);
      setCurrentStep(0);
      setDynamicFlow([]);
      setAnswers({});
    }
    localStorage.setItem('fitpal_intake_form_is_back_clicked','yes')
  }, [currentStep, primaryGoal]);

  const handleSubmit = useCallback(async (contactData) => {
    setIsSubmitting(true);
    
    try {
      // Format answers for submission
      const formattedAnswers = Object.entries(answers).flatMap(([questionId, answer]) => {
        const split = splitHeightWeightAnswer(answer);
        if (split) return split;
        let formattedAnswer;

        if (Array.isArray(answer)) {
          formattedAnswer = formatMultiSelectAnswer(answer);
        } else if (typeof answer === 'object' && answer !== null) {
          if (answer.preview) {
            formattedAnswer = `File: ${answer.fileName || 'uploaded'}`;
          } else if (answer.text !== undefined) {
            formattedAnswer = answer.text + (answer.imageData ? ' [Image attached]' : '');
          } else {
            formattedAnswer = JSON.stringify(answer);
          }
        } else {
          formattedAnswer = String(answer);
        }

        return [{
          question: questionId,
          answer: formattedAnswer,
          slide_id: questionId,
          rawAnswer: answer
        }];
      });

      const glpMeds = answers['current-glp-medications'] || [];
      const isNewPatient = Array.isArray(glpMeds) && glpMeds.includes('none');
      const flow = isNewPatient ? 'glp1_new' : 'glp1_existing';
      const paymentId = localStorage.getItem('fitpal_intake_payment_id') || '';
      const result = await apiService.submitForm(formattedAnswers, contactData, paymentId, flow, 'weightloss');
      setVisitResult(result);

      setSubmissionId(result.id);
      // §embed-bridge: tell the dashboard what was created. Waits for any
      // in-flight photo relay first — the parent closes its modal on this
      // message, which unmounts the iframe and would abort the upload.
      emitVisitCompleteWhenPhotosSettle({
        masterId: result?.masterId,
        visitId: result?.visitId,
        scheduleLink: result?.scheduleLink,
      });
      clearSavedProgress();
      setSlideDirection('forward');
      setSlideKey(prev => prev + 1);
      setIsComplete(true);
    } catch (error) {
      console.error('Submission error:', error);
      const data = error.response?.data;
      if (data?.errors && Array.isArray(data.errors)) {
        alert(`We can't proceed:\n\n• ${data.errors.join('\n• ')}`);
      } else {
        const errorMessage = data?.msg || data?.error || error.message || 'Failed to submit form';
        alert(`There was an error submitting your information: ${errorMessage}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [answers, clearSavedProgress]);

  const handleStartOver = useCallback(() => {
    setSlideDirection('backward');
    setSlideKey(prev => prev + 1);
    setPrimaryGoal(null);
    setCurrentStep(0);
    setAnswers({});
    setIsComplete(false);
    setSubmissionId(null);
    setIsDisqualified(false);
    setDisqualifyReason('');
    setDynamicFlow([]);
    clearSavedProgress();
  }, [clearSavedProgress]);

  // Get animation class based on direction
  const getAnimationClass = () => {
    switch (slideDirection) {
      case 'forward':
        return 'slide-forward';
      case 'backward':
        return 'slide-backward';
      default:
        return 'slide-initial';
    }
  };

  // Should skip pregnancy questions if male
  const shouldSkipQuestion = useCallback((question) => {
    const sexAtBirth = answers['sex-at-birth'];
    
    // Skip pregnancy status question for males
    if (question.id === 'pregnancy-status') {
      if (sexAtBirth === 'male') {
        return true;
      }
    }
    
    // Skip pregnancy consent for males OR if pregnancy status is 'no'
    if (question.id === 'pregnancy-consent') {
      const pregnancyStatus = answers['pregnancy-status'];
      if (sexAtBirth === 'male' || pregnancyStatus === 'no') {
        return true;
      }
    }
    return false;
  }, [answers]);

  // Find next valid step (skipping conditional steps that don't apply)
  const findNextValidStep = useCallback((fromStep) => {
    let nextStep = fromStep + 1;
    while (nextStep < currentFlow.length) {
      const question = currentFlow[nextStep];
      if (!shouldSkipQuestion(question)) {
        return nextStep;
      }
      nextStep++;
    }
    return nextStep;
  }, [currentFlow, shouldSkipQuestion]);

    const [loading, setLoading] = useState(false);

  const renderStep = () => {
    // Show disqualified screen
    if (isDisqualified) {
      return (
        <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
          <DisqualifiedStep 
            reason={disqualifyReason} 
            onStartOver={handleStartOver}
          />
        </div>
      );
    }

    if (isComplete) {
      return (
        <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
          <SuccessStep 
            submissionId={submissionId} 
            answers={answers}
            flowTitle={getFlowTitle()}
            onStartOver={handleStartOver}
          />
        </div>
      );
    }

    // Show main question if no primary goal selected
    if (!primaryGoal) {
      return (
        <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
          {preview.frame(mainQuestion, (
            <QuestionStep
              question={preview.withIcons(mainQuestion)}
              value={null}
              onAnswer={(answer) => handlePrimaryGoalSelect(answer)}
              showIcons={true}
            />
          ))}
          
          {/* Follow-up link for returning patients */}
          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500 mb-3">Already a patient?</p>
            <a
              href={dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-medium text-sm"
              data-testid="followup-link"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Complete Follow-Up Questionnaire
            </a>
          </div>
        </div>
      );
    }

    // Show current flow question
    if (!currentQuestion) return null;

    // Skip questions that don't apply
    if (shouldSkipQuestion(currentQuestion)) {
      setTimeout(() => {
        const nextValid = findNextValidStep(currentStep);
        if (nextValid < totalSteps) {
          setCurrentStep(nextValid);
        }
      }, 0);
      return null;
    }

    const commonProps = {
      question: preview.withIcons(currentQuestion),
      value: answers[currentQuestion.id],
      onAnswer: (answer) => handleAnswer(currentQuestion.id, answer),
      onNext: handleNext,
      onBack: handleBack,
      canGoBack: true,
      isLastStep: currentStep === totalSteps - 1,
      setLoading:setLoading
    };

    const stepContent = (() => {
      switch (currentQuestion.type) {
        case 'single-select':
        case 'single-select-conditional':
          return <QuestionStep {...commonProps} />;
        case 'multi-select':
        case 'multi-select-conditional':
          return <MultiSelectStep {...commonProps} />;
        case 'multi-select-with-optional-addons':
          return <MultiSelectWithAddonsStep {...commonProps} />;
        case 'regimen-dropdown':
          return <RegimenDropdownStep {...commonProps} answers={answers} />;
        case 'decision-with-regimen':
          return <DecisionWithRegimenStep {...commonProps} answers={answers} />;
        case 'regimen-info-display':
          return <RegimenInfoDisplayStep {...commonProps} answers={answers} />;
        case 'input':
        case 'number':
        case 'date':
        case 'text':
          return <InputStep {...commonProps} />;
        case 'textarea':
          return <TextareaStep {...commonProps} />;
        case 'textarea-with-upload':
          return <TextareaWithUploadStep {...commonProps} />;
        case 'height-weight':
          return <HeightWeightStep {...commonProps} />;
        case 'height-weight-bmi':
          return (
            <HeightWeightBmiStep 
              {...commonProps} 
              onDisqualify={handleDisqualify}
              isOnTreatment={
                Array.isArray(answers['current-glp-medications']) && 
                (answers['current-glp-medications'].includes('semaglutide') || 
                 answers['current-glp-medications'].includes('tirzepatide'))
              }
            />
          );
        case 'consent-dropdown':
          return (
            <ConsentDropdownStep 
              {...commonProps}
              onDisqualify={handleDisqualify}
            />
          );
        case 'medication-selection':
          return <MedicationSelectionStep {...commonProps} />;
        case 'shipping-address':
          return <ShippingAddressStep {...commonProps} setIsComplete={setIsComplete} answers={answers}  setLoading={setLoading} clearSavedProgress={clearSavedProgress} setAnswers={setAnswers}/>;
        case 'file-upload':
          return <FileUploadStep {...commonProps} />;
        case 'basic-contact':
          return <BasicContactStep {...commonProps} />;
        case 'checkout':
          return (
            <GlpCheckout
              onComplete={async (payment) => {
                // Square charged successfully → create the Beluga visit
                // BEFORE advancing to the Complete page. Without this call the
                // patient pays but no beluga_visit row / doctor review ever
                // happens. Only advance on 2xx; surface error on 4xx/5xx.
                try {
                  setIsSubmitting(true);
                  setPaymentData(payment);
                  setAnswers(prev => ({ ...prev, 'payment': payment }));

                  const formattedAnswers = Object.entries(answers).flatMap(([questionId, answer]) => {
                    const split = splitHeightWeightAnswer(answer);
                    if (split) return split;
                    const slide = currentFlow.find(q => q.id === questionId);
                    const consentLabel = consentAnswerLabel(slide, answer);
                    let formattedAnswer;
                    if (consentLabel !== null) {
                      formattedAnswer = consentLabel;
                    } else if (Array.isArray(answer)) {
                      formattedAnswer = formatMultiSelectAnswer(answer);
                    } else if (typeof answer === 'object' && answer !== null) {
                      if (answer.preview) {
                        formattedAnswer = `File: ${answer.fileName || 'uploaded'}`;
                      } else if (answer.text !== undefined) {
                        formattedAnswer = answer.text + (answer.imageData ? ' [Image attached]' : '');
                      } else {
                        formattedAnswer = JSON.stringify(answer);
                      }
                    } else {
                      formattedAnswer = String(answer);
                    }
                    return [{ question: questionId, answer: formattedAnswer, slide_id: questionId, rawAnswer: answer }];
                  });

                  const contactInfo = answers['basic-contact'] || {};
                  const shipping = answers['shipping-address'] || {};
                  const glpMeds = answers['current-glp-medications'] || [];
                  const isNewPatient = Array.isArray(glpMeds) && glpMeds.includes('none');
                  const flow = isNewPatient ? 'glp1_new' : 'glp1_existing';
                  const paymentId = payment?.paymentId || payment?.payment_id || localStorage.getItem('fitpal_intake_payment_id') || '';

                  const submitResult = await apiService.submitForm(formattedAnswers, {
                    first_name: contactInfo.name?.split(' ')[0] || '',
                    last_name: contactInfo.name?.split(' ').slice(1).join(' ') || '',
                    email: contactInfo.email || '',
                    phone: contactInfo.phone || '',
                    dob: contactInfo.dob || '',
                    address: shipping.address1 || '',
                    address2: shipping.address2 || '',
                    city: shipping.city || '',
                    state: shipping.state || '',
                    zip: shipping.zipCode || '',
                  }, paymentId, flow, 'weightloss',
                  // §catalog: forward the exact catalog selection so the BE
                  // builds patientPreference from these medIds (REPLACE) and the
                  // charge already priced from the same set. Falls back to the
                  // legacy dose-string path when absent.
                  {
                    ...((Array.isArray(payment?.selectedMedIds) && payment.selectedMedIds.length)
                      ? { selectedMedIds: payment.selectedMedIds }
                      : {}),
                    // §dexa-add-on: forward what the patient actually bought.
                    // Without this the add-on is CHARGED (SquarePaymentForm gets
                    // amount=totalCents, which includes it) while the record of
                    // what it was for never leaves the browser.
                    dexaAddOn: payment?.dexaAddOn,
                    conciergeAddOn: payment?.conciergeAddOn,
                  });
                  setVisitResult(submitResult);

                  // Capture identifiers so post-checkout photo slides can route
                  // to /beluga/upload-visit-photo-submission. Persist to
                  // localStorage so the post-payment page reload (which
                  // unmounts this component before walking the photo slides)
                  // can rehydrate them on remount.
                  const ids = {
                    masterId: submitResult?.masterId ?? submitResult?.master_id ?? null,
                    visitId: submitResult?.visitId ?? submitResult?.visit_id ?? null,
                  };
                  setVisitIdentifiers(ids);
                  // Photos (ID + full-body) are now collected BEFORE checkout, so
                  // relay them to Beluga here once we have the masterId (the
                  // per-slide handler couldn't — no masterId existed yet at the
                  // slide). Fire-and-forget; autosave persists the photo too.
                  if (ids.masterId) {
                    ['identity-verification', 'full-body-image'].forEach((slideId) => {
                      const photo = answers[slideId];
                      if (photo && photo.preview && !uploadedPhotoSlides.has(slideId)) {
                        setUploadedPhotoSlides(prev => new Set(prev).add(slideId));
                        apiService
                          .uploadVisitPhoto({ masterId: ids.masterId, visitId: ids.visitId, slideId, photo })
                          .catch((err) => console.warn(`[uploadVisitPhoto] ${slideId} relay failed:`, err?.response?.data || err.message));
                      }
                    });
                  }
                  try {
                    localStorage.setItem('fitpal_intake_visit_ids', JSON.stringify(ids));
                  } catch (e) {
                    // localStorage write failures are non-fatal — photo upload
                    // will fall back to skipping with a console warning.
                  }

                  handleNext();
                  clearSavedProgress();
                } catch (error) {
                  console.error('Visit creation error:', error);
                  // §embed-bridge: relay the RAW backend error — the dashboard reads
                  // errorCode/refundId to tell the patient their card was refunded.
                  emitVisitError(error);
                  alert(describeSubmitFailure(error));
                } finally {
                  setIsSubmitting(false);
                }
              }}
              isProcessing={isSubmitting}
              answers={answers}
              setLoading={setLoading}
              clearSavedProgress={clearSavedProgress}
              primaryGoal={primaryGoal}
              currentStep={currentStep}
              timestamp={Date.now()}
            />
          );
        case 'complete':
          return (
            <CompleteStep
              STORAGE_KEY={STORAGE_KEY}
              answers={answers}
              visitType="weightloss"
              visitResult={visitResult}
              onComplete={() => {}}
            />
          );
        case 'preview-interstitial':
          return <PreviewInterstitial step={currentQuestion} onNext={handleNext} />;
        default:
          return <QuestionStep {...commonProps} />;
      }
    })();

    return (
      <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
        {preview.frame(currentQuestion, stepContent, handleBack)}
      </div>
    );
  };



  return (
    <>
<div className='intake-form-loader' style={{ display: loading ? '' : 'none' }}>
  <MutatingDots />
</div>

   <div className={`min-h-screen flex flex-col ${preview.rootClass}`} data-testid="health-intake-form" style={{ display: loading===false ? '' : 'none' }}>
    
      {/* Resume Modal */}
      {showResumeModal && (
        <ResumeModal 
          onResume={handleResumeProgress}
          onStartFresh={handleStartFresh}
        />
      )}

      {/* Header */}
      <header className="w-full py-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          {/* Back button */}
          {(currentStep > 0 || primaryGoal) && !isComplete && !isDisqualified ? (
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
              data-testid="back-button"
              style={{display: currentQuestion?.type==='complete' ? 'none' : ''}}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Back</span>
            </button>
          ) : (
            <div className="w-16 flex-shrink-0" />
          )}

          {/* Logo */}
          <div className="text-center flex-1">
            <BrandLogo
              alt="FitPal"
              className="mx-auto"
              style={{ width: '400px', height: 'auto', maxWidth: '100%' }}
            />
            {primaryGoal && !isComplete && !isDisqualified && (
              <p className="text-xs text-gray-500 mt-1">{getFlowTitle()}</p>
            )}
          </div>

          <div className="w-16 flex-shrink-0" />
        </div>
      </header>

      {/* Progress Bar - only show when in a flow */}
      {primaryGoal && !isComplete && !isDisqualified && totalSteps > 0 && (
        <div className="w-full px-4 py-2">
          <div className="max-w-2xl mx-auto">
            {preview.progress(currentQuestion, currentStep, totalSteps)}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-start px-4 py-8">
    <div className={`w-full ${currentQuestion && currentQuestion.type === 'checkout' ? 'max-w-2xl' : 'max-w-xl'} slide-container`}>
          {renderStep()}
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div> 
   
     
  
    </>
 
  );
};

export default HealthIntakeForm;
